import { useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, Circle, CircleCheck, KeyRound, TimerOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";
import { passwordRules, ResetPasswordFormData, resetPasswordSchema } from "@/model/schemas";
import { authService } from "@/services/authService";
import { authErrorMessage, authErrorStatus } from "@/util/authError";
import { zodFormResolver } from "@/util/form";
import AuthCard from "./auth/AuthCard";
import CtaLink, { ctaVariants } from "./site/CtaLink";
import { TextField as BaseTextField } from "./site/FormFields";

const TextField = BaseTextField<ResetPasswordFormData>;

const PasswordChecklist = () => {
  const password = useWatch<ResetPasswordFormData, "password">({ name: "password" }) ?? "";

  return (
    <ul className="grid gap-1.5" aria-label="Password requirements">
      {passwordRules.map((rule) => {
        const met = rule.isMet(password);
        const Icon = met ? CircleCheck : Circle;
        return (
          <li
            key={rule.label}
            className={cn("flex items-center gap-2 text-[13px]", met ? "text-brand-ink-strong" : "text-slate-500")}
          >
            <Icon className="size-4 shrink-0" aria-hidden />
            {rule.label}
          </li>
        );
      })}
    </ul>
  );
};

const ResetPassword = () => {
  const { refreshAuthStatus } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [linkExpired, setLinkExpired] = useState(!token);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const form = useForm<ResetPasswordFormData>({
    resolver: zodFormResolver<ResetPasswordFormData>(resetPasswordSchema),
    mode: "onTouched",
    defaultValues: { password: "", confirmPassword: "" },
  });

  const onSubmit = async (data: ResetPasswordFormData) => {
    if (!token) {
      setLinkExpired(true);
      return;
    }
    setErrorMessage(null);
    try {
      await authService.resetPassword(token, data.password);
      await refreshAuthStatus();
      navigate("/dashboard");
    } catch (error) {
      if (authErrorStatus(error) === 410) {
        setLinkExpired(true);
        return;
      }
      setErrorMessage(authErrorMessage(error));
    }
  };

  if (linkExpired) {
    return (
      <AuthCard
        icon={TimerOff}
        title="This link has expired"
        subtitle="Password links work once and expire after 1 hour. Request a new one and we'll email it to you straight away."
      >
        <CtaLink to="/forgot-password" className="w-full">
          Send a new link
        </CtaLink>
      </AuthCard>
    );
  }

  return (
    <AuthCard icon={KeyRound} title="Choose a new password" subtitle="You'll be signed in as soon as it's saved.">
      <FormProvider {...form}>
        <form noValidate onSubmit={form.handleSubmit(onSubmit)} className="grid gap-6">
          <div>
            <TextField name="password" label="New password" type="password" autoComplete="new-password" />
            <div className="mt-3">
              <PasswordChecklist />
            </div>
          </div>
          <TextField name="confirmPassword" label="Confirm new password" type="password" autoComplete="new-password" />
          {errorMessage && (
            <p role="alert" className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
              {errorMessage}
            </p>
          )}
          <button
            type="submit"
            disabled={form.formState.isSubmitting}
            className={ctaVariants({ className: "w-full" })}
          >
            {form.formState.isSubmitting ? "Saving…" : "Save and sign in"}
            <ArrowRight className="size-4" aria-hidden />
          </button>
        </form>
      </FormProvider>
    </AuthCard>
  );
};

export default ResetPassword;
